// ApprovalCenter — the §7 "Needs your authorization" queue for the AI
// workforce. A generalized, black-amber successor to AgentToolApprovalsPanel
// (which was framed as A2P-only). Reads the SAME real table so nothing is
// fabricated: every card is a real pending row in agent_tool_runs.
//
// Honesty (AI_ACTION_AUDIT_SPEC / AI_AUTONOMY_POLICY): shows only fields that
// exist — the proposed tool, its preview/inputs, and expiry. It does NOT
// invent risk/impact/confidence scores that aren't in the row. Approve/Reject
// write the real status transition (RLS-scoped); the agent's next call
// executes an approved token or gets a 410 on reject.

import React from 'react';
import { supabase } from '../lib/supabase';

const C = {
  base: '#070709', panel: '#0A0D10', card: '#0D1115',
  amber: '#F5A623', bright: '#FFC457', deep: '#A8892E',
  text: '#F4F4F2', dim: '#A7ADB5', faint: '#5A6472',
  hair: 'rgba(245,166,35,0.16)', hairStrong: 'rgba(245,166,35,0.34)',
  critical: '#C0554D',
};
const mono = 'JetBrains Mono, ui-monospace, monospace';

function humanTool(t) {
  return String(t || 'action').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function asText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
function timeLeft(expires_at) {
  if (!expires_at) return null;
  const ms = new Date(expires_at).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const m = Math.round(ms / 60000);
  return m < 60 ? `${m}m left` : `${Math.round(m / 60)}h left`;
}

export default function ApprovalCenter() {
  const [uid, setUid] = React.useState(null);
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async (ownerId) => {
    const id = ownerId || uid;
    if (!id) return;
    const { data } = await supabase
      .from('agent_tool_runs')
      .select('id, tool, status, input, preview, expires_at, created_at')
      .eq('owner_id', id)
      .eq('status', 'pending_approval')
      .order('created_at', { ascending: false })
      .limit(25);
    setRuns(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [uid]);

  React.useEffect(() => {
    let ch;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const id = data?.user?.id;
      if (!id) { setLoading(false); return; }
      setUid(id);
      await load(id);
      ch = supabase.channel(`approval_center_${id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'agent_tool_runs', filter: `owner_id=eq.${id}` }, () => load(id))
        .subscribe();
    })();
    return () => { try { if (ch) supabase.removeChannel(ch); } catch { /* noop */ } };
  }, []); // eslint-disable-line

  const approve = async (run) => {
    setBusyId(run.id);
    try {
      await supabase.from('agent_tool_runs')
        .update({ status: 'approved', approved_at: new Date().toISOString(), approved_by: uid })
        .eq('id', run.id);
      await load();
    } finally { setBusyId(null); }
  };
  const reject = async (run) => {
    const reason = window.prompt('Reason for rejection (optional):', '') || '';
    setBusyId(run.id);
    try {
      await supabase.from('agent_tool_runs')
        .update({ status: 'rejected', rejected_at: new Date().toISOString(), rejection_reason: reason || null })
        .eq('id', run.id);
      await load();
    } finally { setBusyId(null); }
  };

  if (loading) return null;

  return (
    <div style={{
      background: `linear-gradient(180deg, ${C.panel}, ${C.base})`,
      border: `1px solid ${runs.length ? C.hairStrong : C.hair}`,
      borderRadius: 14, padding: '16px 18px', marginBottom: 20,
      boxShadow: 'inset 0 1px 0 rgba(245,166,35,0.10)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: runs.length ? 14 : 0 }}>
        <span aria-hidden style={{ width: 7, height: 7, borderRadius: 999, background: runs.length ? C.amber : C.faint, boxShadow: runs.length ? `0 0 10px ${C.amber}` : 'none' }} />
        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.bright, fontWeight: 700 }}>
          Needs your authorization
        </div>
        {runs.length > 0 && (
          <span style={{ fontFamily: mono, fontSize: 10, color: '#0b0b0b', background: C.amber, borderRadius: 999, padding: '1px 8px', fontWeight: 800 }}>
            {runs.length}
          </span>
        )}
      </div>

      {runs.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.dim }}>
          Nothing is waiting on you. When an AI employee wants to do something high-impact
          (submit A2P, buy a number, send a bulk message), it pauses here for your sign-off.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {runs.map((run) => {
            const tl = timeLeft(run.expires_at);
            const expired = tl === 'expired';
            return (
              <div key={run.id} style={{ background: C.card, border: `1px solid ${C.hair}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{humanTool(run.tool)}</div>
                  {tl && <div style={{ marginLeft: 'auto', fontFamily: mono, fontSize: 10, color: expired ? C.critical : C.faint }}>{tl}</div>}
                </div>
                {run.preview != null && (
                  <pre style={{
                    margin: '0 0 6px', fontSize: 11.5, color: '#d7d5cd', lineHeight: 1.5,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
                    maxHeight: 120, overflow: 'auto',
                  }}>{asText(run.preview).slice(0, 600)}</pre>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => approve(run)} disabled={busyId === run.id || expired} style={{
                    padding: '8px 16px', borderRadius: 8, cursor: expired ? 'not-allowed' : 'pointer',
                    background: expired ? '#2a2a2a' : `linear-gradient(180deg, ${C.bright}, ${C.deep})`,
                    color: expired ? C.faint : '#0b0b0b', border: 'none', fontWeight: 800, fontSize: 12.5,
                    opacity: busyId === run.id ? 0.6 : 1,
                  }}>Approve</button>
                  <button onClick={() => reject(run)} disabled={busyId === run.id} style={{
                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                    background: 'transparent', color: C.dim, border: `1px solid ${C.hair}`,
                    fontWeight: 600, fontSize: 12.5, opacity: busyId === run.id ? 0.6 : 1,
                  }}>Reject</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
